import { z } from "zod";
import {
  LineModifiersSchema,
  OrderSchema,
  OrderSourceSchema,
  OrderStatusSchema,
  PaymentMethodSchema,
} from "../entities/order.js";

export const CreateOrderLineSchema = z.object({
  menuItemId: z.string().uuid(),
  quantity: z.number().int().positive().max(100),
  modifiers: LineModifiersSchema.default({}),
});
export type CreateOrderLine = z.infer<typeof CreateOrderLineSchema>;

export const PaymentInputSchema = z.object({
  method: PaymentMethodSchema,
  tenderedCents: z.number().int().nonnegative().optional(),
});
export type PaymentInput = z.infer<typeof PaymentInputSchema>;

export const HubReceivedAtSchema = z.string().datetime({ offset: true });

export const CreateOrderRequestSchema = z.object({
  source: OrderSourceSchema,
  customerName: z.string().min(1).max(200),
  customerPhone: z.string().min(1).max(32),
  customerAddress: z.string().max(500).nullable().default(null),
  homeDelivery: z.boolean().default(false),
  notes: z.string().max(1000).nullable().default(null),
  items: z.array(CreateOrderLineSchema).min(1).max(100),
  tableId: z.string().uuid().optional(),
  payment: PaymentInputSchema.optional(),
  clientId: z.string().uuid().toLowerCase().optional(),
  hubTicketNumber: z.number().int().positive().max(99999).optional(),
  hubReceivedAt: HubReceivedAtSchema.optional(),
}).superRefine((body, ctx) => {
  if (body.hubReceivedAt !== undefined && body.hubTicketNumber === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["hubTicketNumber"],
      message: "Only a hub order carries the time the hub received it",
    });
  }
  if (body.hubTicketNumber !== undefined && body.clientId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["clientId"],
      message: "A hub order needs its client id",
    });
  }
});
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const AddOrderItemsRequestSchema = z.object({
  clientId: z.string().uuid().toLowerCase(),
  items: z.array(CreateOrderLineSchema).min(1).max(100),
});
export type AddOrderItemsRequest = z.infer<typeof AddOrderItemsRequestSchema>;

export const AddOrderItemsResponseSchema = z.object({
  order: OrderSchema,
  additionId: z.string().uuid(),
});
export type AddOrderItemsResponse = z.infer<typeof AddOrderItemsResponseSchema>;

export const UpdateOrderStatusRequestSchema = z.object({
  status: OrderStatusSchema,
  hubChangedAt: HubReceivedAtSchema.optional(),
});
export type UpdateOrderStatusRequest = z.infer<
  typeof UpdateOrderStatusRequestSchema
>;

export const CustomerLookupResponseSchema = z.object({
  name: z.string().nullable(),
  orderCount: z.number().int().nonnegative(),
  firstOrderAt: z.string().nullable(),
});
export type CustomerLookupResponse = z.infer<typeof CustomerLookupResponseSchema>;

export const TOTAL_CHANGED_CODE = "TOTAL_CHANGED";

export const PayOrdersRequestSchema = PaymentInputSchema.extend({
  orderIds: z.array(z.string().uuid()).min(1).max(50),
  expectedTotalCents: z.number().int().nonnegative().optional(),
  clientId: z.string().uuid().toLowerCase().optional(),
  hubPaidAt: HubReceivedAtSchema.optional(),
  keepTableSeated: z.boolean().optional(),
}).superRefine((body, ctx) => {
  if (body.hubPaidAt !== undefined && body.clientId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["clientId"],
      message: "Only a hub payment carries the time the hub took it",
    });
  }
});
export type PayOrdersRequest = z.infer<typeof PayOrdersRequestSchema>;

export const KitchenScreensResponseSchema = z.object({
  open: z.number().int().nonnegative(),
});
export type KitchenScreensResponse = z.infer<typeof KitchenScreensResponseSchema>;
